import React, { useEffect, useState } from 'react';

// One-time "Add to Home Screen" toast for mobile visitors.
// Fully self-contained: reads only localStorage + browser APIs, no App state.
// Never shows: on desktop, when already installed (standalone), on a first
// cold visit (must have completed a daily or be a returning signed-in user),
// or after dismissal (single persistent localStorage key).
// Home-screen only: watches the DOM for the HOME return's .home-screen div
// (App.js renders it exclusively on the home screen), so the toast never
// appears mid-game and the natural trigger is the results -> home transition
// right after a completed daily. If .home-screen is ever renamed in App.js,
// update the selector below.

const DISMISS_KEY = 'kyg_install_prompt_dismissed';
const SHOWN_COUNT_KEY = 'kyg_install_prompt_shown_count';
const SHOW_CAP = 2; // soft cap: after 2 real appearances, never show again
const PLAYED_KEYS = ['kyg_daily', 'kyg_nba_daily', 'kyg_mosh_daily', 'kyg_ntg'];
const AUTH_TOKEN_KEY = 'sb-hwbuokpowygjecbujtfz-auth-token';

const getShownCount = () => {
  try {
    return parseInt(localStorage.getItem(SHOWN_COUNT_KEY), 10) || 0;
  } catch (e) {
    return 0;
  }
};

const bumpShownCount = () => {
  try {
    localStorage.setItem(SHOWN_COUNT_KEY, String(getShownCount() + 1));
  } catch (e) {
    // storage unavailable: count stays best-effort
  }
};

const isStandalone = () => {
  try {
    return (
      (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) ||
      window.navigator.standalone === true
    );
  } catch (e) {
    return false;
  }
};

// lastPlayedDate is written ONLY when a daily completes and is never cleared,
// so any non-null value means "finished at least one daily on this device".
// The auth token presence catches signed-in returners from another device.
const hasPlayedOrReturning = () => {
  try {
    const played = PLAYED_KEYS.some((k) => {
      const raw = localStorage.getItem(k);
      if (!raw) return false;
      try {
        return !!JSON.parse(raw).lastPlayedDate;
      } catch (e) {
        return false;
      }
    });
    return played || !!localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (e) {
    return false;
  }
};

const ShareGlyph = () => (
  <svg className="install-toast-glyph" viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
    <path d="M12 3 L12 14 M12 3 L8.5 6.5 M12 3 L15.5 6.5" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8 10 H5 V21 H19 V10 H16" stroke="currentColor" strokeWidth="1.8" fill="none" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const InstallPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [installEvent, setInstallEvent] = useState(null);

  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isIOS = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);

  useEffect(() => {
    let timer = null;
    let observer = null;
    let shown = false; // true while the toast is up on the current home visit
    // Capture Chrome's install event even before the toast is eligible,
    // and suppress the default mini-infobar.
    const onBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallEvent(e);
    };

    // Re-checked on every home entry, so a guest who just completed their
    // first daily becomes eligible the moment they land back on home.
    const isEligible = () =>
      (isIOS || isAndroid) &&
      !isStandalone() &&
      !localStorage.getItem(DISMISS_KEY) &&
      getShownCount() < SHOW_CAP &&
      hasPlayedOrReturning();

    const sync = () => {
      try {
        const onHome = !!document.querySelector('.home-screen');
        if (!onHome) {
          if (timer) {
            clearTimeout(timer);
            timer = null;
          }
          shown = false;
          setVisible(false);
          return;
        }
        // Already up on this home visit: leave it alone. The shown-count cap
        // only gates FUTURE appearances, it never yanks a visible toast.
        if (shown) return;
        if (isEligible()) {
          if (!timer) {
            timer = setTimeout(() => {
              timer = null;
              shown = true;
              // Counts only real appearances: the timer is cleared if the
              // user leaves home first, and suppressed renders never get here.
              bumpShownCount();
              setVisible(true);
            }, 800);
          }
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      } catch (e) {
        // never let the prompt break the app
      }
    };

    try {
      window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      observer = new MutationObserver(sync);
      observer.observe(document.body, { childList: true, subtree: true });
      sync();
    } catch (e) {
      // never let the prompt break the app
    }
    return () => {
      try {
        window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      } catch (e) {
        // no-op
      }
      if (observer) observer.disconnect();
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch (e) {
      // storage unavailable: hide for this session anyway
    }
    setVisible(false);
  };

  const handleInstall = async () => {
    try {
      if (installEvent) {
        installEvent.prompt();
        await installEvent.userChoice;
      }
    } catch (e) {
      // prompt() can only be called once; treat any failure as handled
    }
    dismiss();
  };

  if (!visible) return null;

  return (
    <div className="install-toast">
      <button className="install-toast-close" onClick={dismiss} aria-label="Dismiss">&times;</button>
      <img className="install-toast-icon" src="/apple-touch-icon.png" alt="" width="48" height="48" />
      <div className="install-toast-body">
        <p className="install-toast-title">Add KYG to your home screen</p>
        {isIOS ? (
          <p className="install-toast-text">
            Tap <ShareGlyph /> Share, then "Add to Home Screen."
          </p>
        ) : installEvent ? (
          <div>
            <p className="install-toast-text">The daily, one tap away.</p>
            <button className="install-toast-btn" onClick={handleInstall}>Add to home screen</button>
          </div>
        ) : (
          <p className="install-toast-text">
            Open your browser menu, then tap "Add to Home screen."
          </p>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
